import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, TextField, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Chip, CircularProgress, Pagination, IconButton, Tooltip, InputAdornment, Rating
} from '@mui/material';
import { Search, Refresh } from '@mui/icons-material';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../services/api';

const statusColor = {
  completed: 'success',
  ongoing: 'info',
  connecting: 'warning',
  scheduled: 'default',
  cancelled: 'error',
};

export default function ConsultationsPage() {
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (status) params.status = status;
      if (type) params.type = type;
      const res = await api.get('/admin/consultations', { params });
      const data = res.data?.data || {};
      setConsultations(data.consultations || []);
      setTotalPages(data.pagination?.pages || 1);
      setTotal(data.pagination?.total || 0);
    } catch (err) {
      console.error('Failed to fetch consultations:', err);
    } finally {
      setLoading(false);
    }
  }, [page, search, status, type]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      setPage(1);
      fetchData();
    }
  };

  return (
    <AdminLayout title="Consultations">
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5">Consultation History ({total})</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search user or astrologer..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleSearch}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>
              }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select value={status} label="Status" onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="ongoing">Ongoing</MenuItem>
                <MenuItem value="connecting">Connecting</MenuItem>
                <MenuItem value="scheduled">Scheduled</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Type</InputLabel>
              <Select value={type} label="Type" onChange={(e) => { setType(e.target.value); setPage(1); }}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="chat">Chat</MenuItem>
                <MenuItem value="call">Voice</MenuItem>
                <MenuItem value="video">Video</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchData}><Refresh /></IconButton>
            </Tooltip>
          </Box>
        </Box>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell>ID</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Astrologer</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Duration</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="center">Rating</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && consultations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No consultations found.
                  </TableCell>
                </TableRow>
              )}
              {!loading && consultations.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>{c.id}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{c.user_name}</Typography>
                    <Typography variant="caption" color="text.secondary">{c.user_email}</Typography>
                  </TableCell>
                  <TableCell>{c.astrologer_name}</TableCell>
                  <TableCell>
                    <Chip label={c.type} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip label={c.status} size="small" color={statusColor[c.status] || 'default'} />
                  </TableCell>
                  <TableCell align="right">
                    {c.duration_minutes ? `${c.duration_minutes} min` : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {c.amount_paid > 0 ? `₹${c.amount_paid.toFixed(2)}` : '-'}
                  </TableCell>
                  <TableCell align="center">
                    {c.rating > 0 ? (
                      <Rating value={c.rating} readOnly size="small" />
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {c.created_at ? new Date(c.created_at).toLocaleDateString('en-IN') : '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} color="primary" />
          </Box>
        )}
      </Container>
    </AdminLayout>
  );
}
